import { create } from 'zustand';

import type { MyAccess } from '../lib/access/myAccess';

/**
 * What the signed-in person can (PLAN-MULTI-TENANT § 10.2, lot L6g): the single front copy of `my_access()`, read by
 * the route guard and every menu through `useMyAccess`. Keyed by the account it was read for, so that another account
 * signing in on the same tab never inherits the previous one's rights.
 *
 * `idle`: never asked. `error.message` is for the screen, in French.
 */
export type AccessState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading'; readonly userId: string }
  | { readonly status: 'ready'; readonly userId: string; readonly access: MyAccess }
  | { readonly status: 'error'; readonly userId: string; readonly message: string };

interface AccessStore {
  readonly state: AccessState;
  readonly startLoading: (userId: string) => void;
  readonly setAccess: (userId: string, access: MyAccess) => void;
  readonly setError: (userId: string, message: string) => void;
  readonly reset: () => void;
}

const IDLE: AccessState = { status: 'idle' };

/** State and pure setters only; the read lives in `useMyAccess` (ENGINEERING.md § 5). An answer for an account that
 *  is no longer the one being read is dropped. */
export const useAccessStore = create<AccessStore>()((set) => ({
  state: IDLE,
  startLoading: (userId) => set({ state: { status: 'loading', userId } }),
  setAccess: (userId, access) =>
    set((s) => (s.state.status === 'loading' && s.state.userId === userId ? { state: { status: 'ready', userId, access } } : s)),
  setError: (userId, message) =>
    set((s) => (s.state.status === 'loading' && s.state.userId === userId ? { state: { status: 'error', userId, message } } : s)),
  reset: () => set({ state: IDLE }),
}));
